"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Partnership() {
  const {
    ref: headRef,
    isVisible: headVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const values = [{
    title: "Global Product Sourcing",
    description: "We bridge the gap between innovative global manufacturers and local healthcare providers, ensuring access to premium medical technologies.",
    tilt: "-rotate-[1.5deg]",
    icon: <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="w-7 h-7">
          <path d="M16 2v28M2 16h28" />
          <circle cx="16" cy="16" r="10" />
        </svg>
  }, {
    title: "Clinical Product Evaluation",
    description: "Our team rigorously evaluates every device for clinical efficacy and safety, ensuring only the most reliable solutions reach your facility.",
    tilt: "rotate-0",
    icon: <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="w-7 h-7">
          <path d="M12 22l4-4 4 4" />
          <path d="M16 18V6" />
          <path d="M22 12l-6-6-6 6" />
        </svg>
  }, {
    title: "Regulatory Support",
    description: "We navigate complex international regulatory landscapes, providing the documentation and compliance expertise needed for seamless distribution.",
    tilt: "rotate-0",
    icon: <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="w-7 h-7">
          <rect x="6" y="4" width="20" height="24" rx="2" />
          <path d="M10 10h12M10 16h12M10 22h8" />
        </svg>
  }, {
    title: "Long-term Partnerships",
    description: "We build enduring relationships with distributors and healthcare professionals, fostering growth through shared success and trust.",
    tilt: "rotate-[1.5deg]",
    icon: <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="w-7 h-7">
          <path d="M16 16c4 0 8-4 8-8s-4-8-8-8-8 4-8 8 4 8 8 8z" />
          <path d="M4 30c0-6 6-10 12-10s12 4 12 10" />
        </svg>
  }];
  return <section data-section-id="1147" id="membershipbenefitsregistration" className="relative overflow-hidden bg-background text-foreground py-24 md:py-32 px-6 md:px-10 lg:px-16">
      <svg className="absolute top-0 left-0 w-full h-6 text-background" viewBox="0 0 1320 24" preserveAspectRatio="none" aria-hidden="true">
        <path d="M0,24 C82.5,2 165,2 247.5,12 C330,22 412.5,2 495,8 C577.5,14 660,2 742.5,10 C825,18 907.5,2 990,12 C1072.5,22 1155,4 1237.5,10 C1280,13 1310,18 1320,24 L1320,0 L0,0 Z" fill="currentColor" />
      </svg>

      <svg className="absolute top-32 right-[10%] w-10 h-10 text-primary/60 pointer-events-none" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" aria-hidden="true">
        <circle cx="16" cy="16" r="4" />
        <path d="M16 4v4M16 24v4M4 16h4M24 16h4M7 7l3 3M22 22l3 3M7 25l3-3M22 10l3-3" />
      </svg>
      <svg className="absolute bottom-40 right-[6%] w-12 h-12 text-primary/50 pointer-events-none" viewBox="0 0 40 40" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" aria-hidden="true">
        <path d="M4 24c4-4 8-4 12 0s8 4 12 0 8-4 12 0" />
      </svg>

      <div className="relative max-w-[1320px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
        <div ref={headRef} className={`lg:col-span-5 transition-all duration-700 ease-out ${headVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <div className="font-default text-xs md:text-sm uppercase tracking-[0.18em] text-muted-foreground mb-6">
             04 — OUR STRATEGIC VALUE
          </div>

          <h2 className="font-default font-semibold text-4xl md:text-5xl lg:text-[3.5rem] leading-[1.05] tracking-tight text-foreground">
            Connecting global
            <br />
            innovation to{" "}
            <span className="relative inline-block">
              <span className="relative z-10">healthcare</span>
              <svg className="absolute -inset-x-3 -inset-y-2 w-[calc(100%+1.5rem)] h-[calc(100%+1rem)] pointer-events-none" viewBox="0 0 140 70" fill="none" aria-hidden="true">
                <ellipse cx="70" cy="36" rx="62" ry="28" stroke="var(--color-primary)" strokeWidth="3" strokeLinecap="round" transform="rotate(-3 70 36)" strokeDasharray="320" strokeDashoffset={headVisible ? "0" : "320"} style={{
                transition: "stroke-dashoffset 700ms ease-out 300ms"
              }} />
              </svg>
            </span>
            .
          </h2>

          <p className="font-default text-lg md:text-xl leading-relaxed text-muted-foreground mt-7 max-w-xl">
            MS Biotechnology serves as your trusted global sourcing and distribution partner. We carefully evaluate and deliver innovative medical technologies, empowering distributors and clinicians worldwide.
          </p>

          <Link to="/CompliancePage" className="group inline-flex items-center gap-2 font-default font-semibold text-foreground mt-8 relative">
            <span className="relative">
              Explore our compliance standards
              <svg className="absolute left-0 -bottom-1.5 w-full h-2 text-primary" viewBox="0 0 200 8" preserveAspectRatio="none" fill="none" aria-hidden="true">
                <path d="M2 5 Q 25 1, 50 5 T 100 5 T 150 5 T 198 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300 ease-out" style={{
                transformBox: "fill-box"
              }} />
              </svg>
            </span>
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>

          <div className="mt-12 lg:mt-16 w-24 h-24" aria-hidden="true">
            <svg viewBox="0 0 120 120" fill="none" className="w-full h-full">
              <path d="M20 80 Q 20 40 60 40 Q 100 40 100 80 Q 100 95 85 95 L 35 95 Q 20 95 20 80 Z" fill="var(--color-secondary)" stroke="var(--color-foreground)" strokeWidth="2.5" strokeLinejoin="round" />
              <path d="M48 40 Q 50 30 58 32 Q 62 24 70 30 Q 78 28 76 38" fill="var(--color-background)" stroke="var(--color-foreground)" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
              <circle cx="48" cy="65" r="3" fill="var(--color-foreground)" />
              <circle cx="72" cy="65" r="3" fill="var(--color-foreground)" />
              <circle cx="40" cy="76" r="4" fill="var(--color-primary)" opacity="0.7" />
              <circle cx="80" cy="76" r="4" fill="var(--color-primary)" opacity="0.7" />
              <path d="M52 78 Q 60 84 68 78" stroke="var(--color-foreground)" strokeWidth="2.5" strokeLinecap="round" fill="none" />
              <g transform="translate(95 70)">
                <path d="M0 -7 L2 -2 L7 -2 L3 1 L5 6 L0 3 L-5 6 L-3 1 L-7 -2 L-2 -2 Z" fill="var(--color-chart-3)" stroke="var(--color-foreground)" strokeWidth="1.5" strokeLinejoin="round" />
              </g>
            </svg>
          </div>
        </div>

        <div ref={gridRef} className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6">
          {values.map((value, i) => <div key={i} data-index={i} className={`group relative bg-card text-card-foreground rounded-lg p-7 md:p-9 shadow-md hover:shadow-lg hover:-translate-y-1 transition-all duration-300 ease-out sm:${value.tilt} hover:rotate-0 ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
          transition: `opacity 700ms ease-out ${i * 90}ms, transform 320ms ease-out, box-shadow 320ms ease-out`,
          opacity: gridVisible ? 1 : 0,
          transform: gridVisible ? undefined : "translateY(16px)"
        }}>
              <svg className="absolute top-3 right-3 w-10 h-10 text-primary opacity-0 group-hover:opacity-100 transition-opacity duration-300" viewBox="0 0 40 40" fill="none" aria-hidden="true">
                <path d="M8 8 Q 20 4, 32 8 M 32 8 Q 36 20, 32 32" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" fill="none" />
              </svg>

              <div className="text-primary mb-5">{value.icon}</div>

              <h3 className="font-default font-bold text-xl md:text-[1.375rem] leading-snug text-foreground mb-3">
                {value.title}
              </h3>

              <p className="font-default text-base leading-relaxed text-muted-foreground">
                {value.description}
              </p>
            </div>)}
        </div>
      </div>
    </section>;
}